import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Browse from "@/pages/Browse";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PublicCourse from "@/pages/PublicCourse";
import PublicJob from "@/pages/PublicJob";
import FunnelPage from "@/pages/FunnelPage";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import MyLearning from "@/pages/MyLearning";
import Jobs from "@/pages/Jobs";
import MyApplications from "@/pages/MyApplications";
import Admin from "@/pages/Admin";
import RoleSelection from "@/pages/RoleSelection";
import StudentDashboard from "@/pages/StudentDashboard";
import EmployerDashboard from "@/pages/EmployerDashboard";
import Footer from "@/components/Footer";
import { Loader2 } from "lucide-react";

function ChooseRoleRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF9]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.needs_onboarding) return <Navigate to="/dashboard" replace />;
  return <RoleSelection />;
}

function RoleHome() {
  const { user } = useAuth();
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  if (user?.role === "employer") return <Navigate to="/employer" replace />;
  return <StudentDashboard />;
}

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Landing />} />
      <Route path="/browse" element={<Browse />} />
      <Route path="/browse/courses/:id" element={<PublicCourse />} />
      <Route path="/browse/jobs/:id" element={<PublicJob />} />
      <Route path="/for-students" element={<FunnelPage seg="student" key="student" />} />
      <Route path="/for-employers" element={<FunnelPage seg="employer" key="employer" />} />
      <Route path="/for-experts" element={<FunnelPage seg="expert" key="expert" />} />
      <Route path="/choose-role" element={<ChooseRoleRoute />} />
      <Route path="/dashboard" element={<ProtectedRoute><RoleHome /></ProtectedRoute>} />
      <Route path="/employer" element={<ProtectedRoute employer><EmployerDashboard /></ProtectedRoute>} />
      <Route path="/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
      <Route path="/courses/:id" element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
      <Route path="/my-learning" element={<ProtectedRoute><MyLearning /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
      <Route path="/my-applications" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute admin><Admin /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function GlobalFooter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return null;
  if (location.pathname === "/") return null; // Landing has its own footer
  return <Footer />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <GlobalFooter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
